import axios from 'axios';
import {API_URL} from '../config/constants';
import SpringDataPage, {BasicPage} from '../@types/spring-data-page';
import {getErrorMessage} from '../utils/utils';
import NewOccurrenceReport from '../@types/new-occurrence-report';
import Group from '../@types/group';
import {makeGroup} from '../utils/group-utils';
import {Moment} from 'moment';
import {DescriptorTerm} from '../@types/descriptor-term';
import {SimpleReportDescriptorTerm} from '../@types/occurrence-report-descriptor';
import {normalizeReportContent} from '../utils/report-content-converters';


class NewOccurrenceService {
    static occurrenceUrlV2 = API_URL + 'api/v2/reports';

    formOccurrence(occurrence: any): NewOccurrenceReport {
        let occur: NewOccurrenceReport = {} as NewOccurrenceReport;
        occur.id = occurrence.id;
        occur.uniqueId = occurrence.uniqueId;
        occur.content = normalizeReportContent(occurrence.sourceName, occurrence.content);
        occur.dateTime = occurrence.datetime;
        occur.created = occurrence.created;
        occur.updated = occurrence.updated;
        occur.latitude = occurrence.latitude;
        occur.longitude = occurrence.longitude;
        occur.facilityName = occurrence.facilityName;
        occur.summary = occurrence.summary;
        occur.sourceName = occurrence.sourceName;
        occur.superseded = occurrence.superseded;
        occur.group = occurrence.group ? makeGroup(occurrence.group) :  new Group();
        occur.descriptorTerms = occurrence.descriptorTerms || [];
        return occur;
    }

    formOccurrencesPagePromise(occurrencesData: any): Promise<SpringDataPage<NewOccurrenceReport>> {
        let occurrencesPage: SpringDataPage<NewOccurrenceReport> = {} as SpringDataPage<NewOccurrenceReport>;
        if (occurrencesData) {
            if (occurrencesData._embedded?.reportDTOList) {
                occurrencesPage.content = occurrencesData._embedded.reportDTOList?.map((occurrence: any) => this.formOccurrence(occurrence));
            } else {
                occurrencesPage.content = [];
            }
            occurrencesPage.page = occurrencesData.page;
        }

        return Promise.resolve(occurrencesPage);
    }

    fetchOccurrences(page: BasicPage, groupId?: number|null, searchTerm?: string, startSearchDate?: Moment | null, endSearchDate?: Moment | null, sourceNames?: string[], descriptorTermIds?: number[], superseded?: boolean): Promise<SpringDataPage<NewOccurrenceReport>> {
        let url = `${NewOccurrenceService.occurrenceUrlV2}/search?`
            + `size=${page.size}&`
            + `page=${page.offset}&`
            + `sort=${page.sort},${page.direction}`;

        if (searchTerm && searchTerm !== '') url += `&searchTerm=${searchTerm}`;
        if (groupId) url += `&groupId=${groupId}`;
        if (startSearchDate) url += `&startDate=${startSearchDate.toISOString()}`;
        if (endSearchDate) url += `&endDate=${endSearchDate.toISOString()}`;
        if (sourceNames && sourceNames.length > 0) {
            sourceNames.forEach(source => {
                url += `&sourceNames=${encodeURIComponent(source)}`;
            });
        }
        if (descriptorTermIds && descriptorTermIds.length > 0) {
            descriptorTermIds.forEach(id => {
                url += `&descriptorTermIds=${id}`;
            });
        }
        if (superseded !== undefined) url += `&superseded=${superseded}`;
        // Explicitly request descriptor terms to be included
        url += '&includeDescriptorTerms=true';
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return this.formOccurrencesPagePromise(response.data);
            })
            .catch(err => {
                throw new Error('Failed to get occurrences: ' + getErrorMessage(err));
            })
    }

    fetchOccurrenceById(occurrenceId: number): Promise<NewOccurrenceReport> {
        let url = `${NewOccurrenceService.occurrenceUrlV2}/${occurrenceId}`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(this.formOccurrence(response.data));
            })
            .catch(err => {
                throw new Error(`Failed to get occurrence ${occurrenceId} ` + getErrorMessage(err));
            })
    }

    fetchReportDescriptorTerms(occurrenceId: number): Promise<any[]> {
        let url = `${NewOccurrenceService.occurrenceUrlV2}/${occurrenceId}/descriptor-terms`;
        return axios
            .get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(response.data.descriptorTerms);
            })
            .catch(err => {
                throw new Error(`Failed to get report descriptor terms for occurrence ${occurrenceId} ` + getErrorMessage(err));
            });
    }

    linkReportDescriptorTerms(
        occurrenceId: number,
        descriptorTerms: SimpleReportDescriptorTerm[]
    ): Promise<any[]> {
        let url = `${NewOccurrenceService.occurrenceUrlV2}/link/${occurrenceId}/descriptor-terms`;
        return axios
            .post(url, descriptorTerms, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            })
            .then(response => {
                return Promise.resolve(response.data.descriptorTerms);
            })
            .catch(err => {
                throw new Error(`Failed to post report descriptor terms with info for occurrence ${occurrenceId} ` + getErrorMessage(err));
            });
    }

    unlinkReportDescriptorTerms(occurrenceId: number, terms: number[]): Promise<void> {
        let url = `${NewOccurrenceService.occurrenceUrlV2}/${occurrenceId}/descriptor-terms`;
        return axios
            .delete(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' },
                data: terms
            })
            .then(() => {
                return;
            })
            .catch(err => {
                throw new Error(`Failed to delete report descriptor terms for occurrence ${occurrenceId} ` + getErrorMessage(err));
            });
    }

    /**
     * Fetch all available report sources with their counts
     * @returns Promise containing array of report sources
     */
    async getReportSources(groupId?: number): Promise<{sourceName: string; reportCount: number; description?: string}[]> {
        const url = groupId !== undefined
            ? `${NewOccurrenceService.occurrenceUrlV2}/sources?groupId=${groupId}`
            : `${NewOccurrenceService.occurrenceUrlV2}/sources`;

        try {
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });

            return response.data;
        } catch (err) {
            throw new Error('Failed to fetch report sources: ' + getErrorMessage(err));
        }
    }

    /**
     * Fetch multiple reports by their IDs in a single request
     * @param reportIds Array of report IDs to fetch
     * @param includeDescriptorTerms Whether to include linked descriptor terms (default: true)
     * @returns Promise containing array of reports found
     */
    async fetchReportsByIds(reportIds: number[], includeDescriptorTerms: boolean = true): Promise<NewOccurrenceReport[]> {
        const url = `${NewOccurrenceService.occurrenceUrlV2}/batch?includeDescriptorTerms=${includeDescriptorTerms}`;

        try {
            const response = await axios.post(url, reportIds, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });

            // Convert response data to NewOccurrenceReport format
            return response.data.map((reportData: any) => this.formOccurrence(reportData));
        } catch (err) {
            throw new Error('Failed to fetch reports by IDs: ' + getErrorMessage(err));
        }
    }

    /**
     * Fetch all available descriptor terms from published descriptor sets
     * Used for populating filter options in the UI
     * @returns Promise containing array of descriptor terms with descriptor names for grouping
     */
    async getAvailableDescriptorTerms(): Promise<DescriptorTerm[]> {
        const url = `${API_URL}api/v2/descriptors/terms/published`;

        try {
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });

            return response.data._embedded?.descriptorTermDTOList || response.data || [];
        } catch (err) {
            throw new Error('Failed to fetch available descriptor terms: ' + getErrorMessage(err));
        }
    }

}

export default new NewOccurrenceService();